import { closePopup, commit, currentPopup, el, ICONS, keepInView, openPopup, Popup } from './popup';

// Every <select> on the site opens our own list instead of the system's (the owner, 2026-10-30). Nothing in a template
// changes: the select stays the field - its value, ngModel / formControlName / (change), its own look when closed - and
// only the list it opens is ours. A press on it is caught at the document (so selects added later are covered too), the
// list is built from its options at that moment, and a pick writes selectedIndex and sends input + change, which is
// everything Angular's select accessor and the (change) handlers listen to.
//
// Left to the browser: a multiple or sized select (it is a list already), a disabled one, one marked data-native (or
// inside [data-native-pickers]), and any press from a touch screen - a phone's own picker is the better one there.
// Keyboard: Space, Enter, Alt+Down or F4 open it; in the list the arrows, Home/End, Page Up/Down, typing to jump,
// Enter to pick, Escape or Tab to leave.

const SEARCH_FROM = 15;

interface Row {
    option: HTMLOptionElement;
    node: HTMLElement;
    text: string;
    disabled: boolean;
    visible: boolean;
}

export function installSelectPicker(doc: Document, isTouch: () => boolean, t: (key: string, fallback: string) => string): void {
    const win = doc.defaultView as Window;
    let select: HTMLSelectElement | null = null;
    let popup: Popup | null = null;
    let list: HTMLElement | null = null;
    let search: HTMLInputElement | null = null;
    let rows: Row[] = [];
    let active = -1;
    let typed = '';
    let typedAt = 0;
    let seq = 0;

    const skip = (s: HTMLSelectElement) =>
        s.multiple || s.size > 1 || s.disabled || s.hasAttribute('data-native') || !!s.closest('[data-native-pickers]');

    const isOpenFor = (s: HTMLSelectElement) => !!popup && select === s && currentPopup() === popup;

    doc.addEventListener('mousedown', e => {
        const target = e.target as Element;
        const s = target && target.closest ? target.closest('select') : null;
        if (!s || e.button !== 0 || skip(s) || isTouch()) {
            return;
        }
        e.preventDefault();
        if (isOpenFor(s)) {
            closePopup();
            return;
        }
        s.focus({ preventScroll: true });
        open(s, false);
    }, true);

    doc.addEventListener('keydown', e => {
        if (popup && select && currentPopup() === popup) {
            onListKey(e);
            return;
        }
        const s = e.target;
        if (!(s instanceof HTMLSelectElement) || skip(s)) {
            return;
        }
        const k = e.key;
        if (k === ' ' || k === 'Enter' || k === 'F4' || ((k === 'ArrowDown' || k === 'ArrowUp') && e.altKey)) {
            e.preventDefault();
            open(s, true);
        }
    }, true);

    function open(s: HTMLSelectElement, _byKey: boolean) {
        select = s;
        seq++;
        const id = 'ph-sel-' + seq;
        const panel = el(doc, 'div', 'ph-sel');
        panel.setAttribute('role', 'presentation');
        // A select drawn see-through over a chip (the inbox's priority) is pointed at by the chip.
        const r = s.getBoundingClientRect();
        const anchor = (r.width < 24 || r.height < 16) && s.parentElement ? s.parentElement : s;

        rows = [];
        list = el(doc, 'div', 'ph-sel__list');
        list.id = id;
        list.setAttribute('role', 'listbox');
        const label = s.getAttribute('aria-label') || (s.labels && s.labels[0] ? s.labels[0].textContent || '' : '');
        if (label) {
            list.setAttribute('aria-label', label.trim());
        }
        for (const child of Array.from(s.children)) {
            if (child instanceof HTMLOptGroupElement) {
                const head = el(doc, 'div', 'ph-sel__group', child.label);
                head.setAttribute('role', 'presentation');
                list.appendChild(head);
                for (const o of Array.from(child.children)) {
                    if (o instanceof HTMLOptionElement) {
                        addRow(o, child.disabled, true);
                    }
                }
            } else if (child instanceof HTMLOptionElement) {
                addRow(child, false, false);
            }
        }

        search = null;
        if (rows.filter(x => !x.disabled).length >= SEARCH_FROM) {
            const box = el(doc, 'div', 'ph-sel__search');
            box.innerHTML = ICONS.search;
            search = el(doc, 'input');
            search.type = 'text';
            search.autocomplete = 'off';
            search.spellcheck = false;
            search.placeholder = t('picker-search', 'Search');
            search.setAttribute('aria-controls', id);
            search.addEventListener('input', () => filter(search ? search.value : ''));
            box.appendChild(search);
            panel.appendChild(box);
        }
        panel.appendChild(list);
        if (!rows.some(x => x.visible)) {
            list.appendChild(el(doc, 'div', 'ph-sel__empty', t('picker-no-options', 'Nothing to choose')));
        }

        list.addEventListener('mousedown', e => e.preventDefault());
        list.addEventListener('mousemove', e => {
            const i = indexOf(e.target as Element);
            if (i >= 0 && i !== active && !rows[i].disabled) {
                setActive(i, false);
            }
        });
        list.addEventListener('click', e => {
            const i = indexOf(e.target as Element);
            if (i >= 0) {
                pick(i);
            }
        });

        s.setAttribute('aria-expanded', 'true');
        s.setAttribute('aria-controls', id);
        const owner = s;
        popup = openPopup(doc, anchor, panel, {
            matchWidth: true,
            maxHeight: 340,
            onClose: () => {
                owner.removeAttribute('aria-expanded');
                owner.removeAttribute('aria-controls');
                if (select === owner) {
                    select = null;
                    popup = null;
                }
            },
        });

        const chosen = rows.findIndex(x => x.option.selected && x.visible);
        setActive(chosen >= 0 ? chosen : firstFrom(0, 1), true, true);
        // A long list opens with its search box ready to type in.
        if (search) {
            search.focus({ preventScroll: true });
        }
    }

    function addRow(o: HTMLOptionElement, groupDisabled: boolean, grouped: boolean) {
        if (o.hidden || o.style.display === 'none' || !list) {
            return;
        }
        const text = (o.label || o.text || '').trim();
        const disabled = o.disabled || groupDisabled;
        const node = el(doc, 'div', 'ph-sel__opt');
        node.id = list.id + '-' + rows.length;
        node.setAttribute('role', 'option');
        node.dataset.i = String(rows.length);
        if (grouped) {
            node.classList.add('ph-sel__opt--grouped');
        }
        // A blank first option ("Choose...", or nothing at all) reads as a hint, not a value.
        if (o.value === '' && (disabled || !text)) {
            node.classList.add('ph-sel__opt--hint');
        }
        const span = el(doc, 'span', 'ph-sel__text', text || '—');
        span.title = text;
        node.appendChild(span);
        if (o.selected) {
            node.classList.add('is-selected');
            node.setAttribute('aria-selected', 'true');
            const tick = el(doc, 'span', 'ph-sel__tick');
            tick.innerHTML = ICONS.check;
            node.appendChild(tick);
        }
        if (disabled) {
            node.classList.add('is-disabled');
            node.setAttribute('aria-disabled', 'true');
        }
        list.appendChild(node);
        rows.push({ option: o, node, text: text.toLocaleLowerCase(), disabled, visible: true });
    }

    function indexOf(target: Element): number {
        const node = target && target.closest ? target.closest('.ph-sel__opt') as HTMLElement : null;
        return node && node.dataset.i ? +node.dataset.i : -1;
    }

    function filter(q: string) {
        const words = q.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
        let any = false;
        for (const r of rows) {
            r.visible = words.every(w => r.text.indexOf(w) >= 0);
            r.node.style.display = r.visible ? '' : 'none';
            any = any || r.visible;
        }
        if (list) {
            list.querySelectorAll('.ph-sel__group').forEach(g => (g as HTMLElement).style.display = words.length ? 'none' : '');
            let empty = list.querySelector('.ph-sel__empty') as HTMLElement;
            if (!any && !empty) {
                empty = el(doc, 'div', 'ph-sel__empty', t('picker-no-match', 'No match'));
                list.appendChild(empty);
            } else if (any && empty) {
                empty.remove();
            }
        }
        setActive(firstFrom(0, 1), true);
        if (popup) {
            popup.place();
        }
    }

    function firstFrom(i: number, step: number): number {
        for (let j = i; j >= 0 && j < rows.length; j += step) {
            if (rows[j].visible && !rows[j].disabled) {
                return j;
            }
        }
        return -1;
    }

    function setActive(i: number, scroll: boolean, center = false) {
        if (active >= 0 && rows[active]) {
            rows[active].node.classList.remove('is-active');
        }
        active = i;
        const target: HTMLElement | null = search || select;
        if (i < 0 || !rows[i]) {
            if (target) {
                target.removeAttribute('aria-activedescendant');
            }
            return;
        }
        rows[i].node.classList.add('is-active');
        if (target) {
            target.setAttribute('aria-activedescendant', rows[i].node.id);
        }
        if (scroll && list) {
            keepInView(list, rows[i].node, center);
        }
    }

    function move(step: number, count = 1) {
        let i = active;
        let found = active;
        for (let n = 0; n < count; n++) {
            const next = firstFrom(i + step, step);
            if (next < 0) {
                break;
            }
            found = i = next;
        }
        if (found < 0) {
            found = firstFrom(step > 0 ? 0 : rows.length - 1, step);
        }
        setActive(found, true);
    }

    function pick(i: number) {
        const s = select;
        const row = rows[i];
        if (!s || !row || row.disabled) {
            return;
        }
        closePopup();
        s.focus({ preventScroll: true });
        commit(s, () => s.selectedIndex = row.option.index);
    }

    function onListKey(e: KeyboardEvent) {
        const k = e.key;
        const inSearch = !!search && e.target === search;
        let handled = true;
        switch (k) {
            case 'ArrowDown': if (e.altKey) { closePopup(); select && select.focus(); } else { move(1); } break;
            case 'ArrowUp': if (e.altKey) { closePopup(); select && select.focus(); } else { move(-1); } break;
            case 'PageDown': move(1, 8); break;
            case 'PageUp': move(-1, 8); break;
            case 'Home': if (inSearch) { handled = false; } else { setActive(firstFrom(0, 1), true); } break;
            case 'End': if (inSearch) { handled = false; } else { setActive(firstFrom(rows.length - 1, -1), true); } break;
            case 'Enter': if (active >= 0) { pick(active); } break;
            case ' ': if (inSearch) { handled = false; } else if (active >= 0) { pick(active); } break;
            case 'Escape': { const s = select; closePopup(); if (s) { s.focus({ preventScroll: true }); } break; }
            case 'Tab': { const s = select; closePopup(); if (s && inSearch) { s.focus({ preventScroll: true }); } handled = false; break; }
            default:
                handled = false;
                if (!inSearch && k.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                    handled = true;
                    typeAhead(k);
                }
        }
        if (handled) {
            e.preventDefault();
            e.stopPropagation();
        }
    }

    function typeAhead(ch: string) {
        const now = Date.now();
        typed = now - typedAt > 700 ? ch.toLocaleLowerCase() : typed + ch.toLocaleLowerCase();
        typedAt = now;
        const same = typed.split('').every(c => c === typed[0]);
        const needle = same ? typed[0] : typed;
        const start = same || active < 0 ? active + 1 : active;
        for (let n = 0; n < rows.length; n++) {
            const i = (start + n) % rows.length;
            const r = rows[i];
            if (r.visible && !r.disabled && r.text.indexOf(needle) === 0) {
                setActive(i, true);
                return;
            }
        }
    }
}
